import { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import "./App.css";
import Landing from "./screens/Landing";
import Quiz from "./screens/Quiz";
import Computing from "./screens/Computing";
import Result from "./screens/Result";
import ResultLoader from "./screens/ResultLoader";
import PaymentSuccess from "./screens/PaymentSuccess";
import PaymentCancel from "./screens/PaymentCancel";
import ReportPage from "./screens/ReportPage";
import AdminStats from "./screens/AdminStats";
import AdminBounced from "./screens/AdminBounced";
import TermsPage from "./screens/TermsPage";
import PrivacyPage from "./screens/PrivacyPage";
import RefundsPage from "./screens/RefundsPage";
import PhilosopherPage from "./screens/PhilosopherPage";
import PhilosopherIndex from "./screens/PhilosopherIndex";
import BlogIndex from "./screens/BlogIndex";
import BlogPost from "./screens/BlogPost";
import { score } from "./lib/scoring";
import { track } from "./lib/analytics";
import { api } from "./lib/api";
import { PHILOSOPHERS } from "./data/philosophers";

const SCREENS = { LANDING: "landing", QUIZ: "quiz", COMPUTING: "computing", RESULT: "result" };

function QuizFlow() {
  const [screen, setScreen] = useState(SCREENS.LANDING);
  const [result, setResult] = useState(null);
  const [resultId, setResultId] = useState(null);
  const navigate = useNavigate();

  const beginTest = useCallback(() => {
    track("quiz_start");
    setScreen(SCREENS.QUIZ);
  }, []);

  const onQuizComplete = useCallback(async (answers) => {
    const r = score(answers);
    setResult(r);
    track("quiz_complete", {
      top: r.matches[0].philosopher.id,
      top_pct: r.matches[0].pct,
    });
    setScreen(SCREENS.COMPUTING);
    // POST to backend in parallel with the computing animation
    try {
      const server = await api.postResult({
        traits: r.user,
        raw: r.raw,
        philosopher: r.matches[0].philosopher.id,
        match_pct: r.matches[0].pct,
        ranking: r.matches.map((m) => ({
          philosopher_id: m.philosopher.id,
          philosopher_name: m.philosopher.name,
          title: m.philosopher.title,
          pct: m.pct,
          r: m.r,
        })),
        answers,
      });
      setResultId(server.result_id);
    } catch (e) {
      // Non-fatal — quiz still shows a result; only the paywall CTA will be disabled.
      console.error(e);
    }
  }, []);

  const onComputingDone = useCallback(() => {
    setScreen(SCREENS.RESULT);
    if (resultId) {
      // Reflect the shareable URL without navigating away.
      window.history.replaceState(null, "", `/result/${resultId}`);
    }
  }, [resultId]);

  const onRetake = useCallback(() => {
    setResult(null);
    setResultId(null);
    navigate("/");
    setScreen(SCREENS.LANDING);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [navigate]);

  return (
    <AnimatePresence mode="wait">
      {screen === SCREENS.LANDING && <Landing key="landing" onBegin={beginTest} />}
      {screen === SCREENS.QUIZ && <Quiz key="quiz" onComplete={onQuizComplete} />}
      {screen === SCREENS.COMPUTING && <Computing key="computing" onDone={onComputingDone} />}
      {screen === SCREENS.RESULT && result && (
        <Result key="result" result={result} resultId={resultId} onRetake={onRetake} />
      )}
    </AnimatePresence>
  );
}

export default function App() {
  useEffect(() => {
    document.documentElement.style.backgroundColor = "#0d0e13";
    document.body.style.backgroundColor = "#0d0e13";
    // Warm up PHILOSOPHERS import
    void PHILOSOPHERS;
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="App" data-testid="app-root">
          <div className="gold-glow" aria-hidden />
          <div className="grain" aria-hidden />
          <Routes>
            <Route path="/" element={<QuizFlow />} />
            <Route path="/philosophers" element={<PhilosopherIndex />} />
            <Route path="/philosophers/:slug" element={<PhilosopherPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/result/:id" element={<ResultLoader />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/report/:id" element={<ReportPage />} />
            <Route path="/admin/stats" element={<AdminStats />} />
            <Route path="/admin/bounced" element={<AdminBounced />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refunds" element={<RefundsPage />} />
            <Route path="*" element={<QuizFlow />} />
          </Routes>
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}
